import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountingDataTable from '../../accounting/shared/AccountingDataTable';
import type { DataTableColumn } from '../../accounting/shared/AccountingDataTable';
import BillingService, {
  BillingItemType,
  BillingProduct,
  SaveBillingProduct,
} from '../../../services/BillingService';

const ITEM_TYPES: { value: BillingItemType; label: string }[] = [
  { value: 'Device', label: 'Cihaz' },
  { value: 'FiscalDevice', label: 'Yazarkasa' },
  { value: 'Contract', label: 'Sözleşme' },
  { value: 'Other', label: 'Diğer' },
];

const itemTypeLabel = (value: string) =>
  ITEM_TYPES.find((t) => t.value === value)?.label || value || 'Diğer';

const formatTry = (n: number) =>
  `${(Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TRY`;

interface ProductForm {
  id?: number;
  name: string;
  description: string;
  defaultPrice: string;
  vatRate: string;
  itemType: BillingItemType;
  isActive: boolean;
}

const emptyForm: ProductForm = {
  name: '',
  description: '',
  defaultPrice: '',
  vatRate: '20',
  itemType: 'Device',
  isActive: true,
};

const ProductsTab: React.FC = () => {
  const [rows, setRows] = React.useState<BillingProduct[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [form, setForm] = React.useState<ProductForm>(emptyForm);
  const [saving, setSaving] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState<BillingProduct | null>(null);
  const [toast, setToast] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      setRows(await BillingService.getProducts(true));
    } catch (e: any) {
      setError(e?.message || 'Ürünler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (p: BillingProduct) => {
    setForm({
      id: p.id,
      name: p.name,
      description: p.description ?? '',
      defaultPrice: String(p.defaultPrice ?? 0),
      vatRate: String(p.vatRate ?? 20),
      itemType: p.itemType || 'Other',
      isActive: p.isActive,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const name = form.name.trim();
    const price = Number(String(form.defaultPrice).replace(',', '.'));
    const vat = Number(String(form.vatRate).replace(',', '.'));
    if (!name) {
      setError('Ürün adı zorunludur.');
      return;
    }
    if (!Number.isFinite(price) || price < 0) {
      setError('Geçerli bir liste fiyatı girin.');
      return;
    }
    if (!Number.isFinite(vat) || vat < 0) {
      setError('Geçerli bir KDV oranı girin.');
      return;
    }

    const payload: SaveBillingProduct = {
      id: form.id,
      name,
      description: form.description.trim() || null,
      defaultPrice: price,
      vatRate: vat,
      currency: 'TRY',
      itemType: form.itemType,
      isActive: form.isActive,
    };

    setSaving(true);
    try {
      await BillingService.saveProduct(payload);
      setDialogOpen(false);
      setToast('Ürün kaydedildi.');
      await load();
    } catch (e: any) {
      setError(e?.message || 'Kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await BillingService.deleteProduct(deleteTarget.id);
      setToast('Ürün silindi.');
      setDeleteTarget(null);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Silinemedi.');
    }
  };

  const columns = React.useMemo<DataTableColumn<BillingProduct>[]>(
    () => [
      { id: 'sn', label: 'SN', width: '56px', align: 'center', render: (_row, index) => index + 1 },
      { id: 'name', label: 'Ürün', minWidth: '200px', render: (p) => p.name },
      { id: 'type', label: 'Tür', render: (p) => itemTypeLabel(p.itemType) },
      {
        id: 'price',
        label: 'Liste fiyatı',
        align: 'right',
        render: (p) => formatTry(p.defaultPrice),
      },
      { id: 'vat', label: 'KDV %', align: 'right', render: (p) => p.vatRate },
      { id: 'status', label: 'Durum', render: (p) => (p.isActive ? 'Aktif' : 'Pasif') },
      {
        id: 'actions',
        label: 'İşlemler',
        width: '110px',
        align: 'center',
        render: (p) => (
          <Box onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Düzenle">
              <IconButton size="small" color="primary" onClick={() => openEdit(p)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sil">
              <IconButton size="small" color="error" onClick={() => setDeleteTarget(p)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    []
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, bgcolor: '#1976d2', color: 'white', px: 2, py: 1, borderRadius: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{rows.length} ürün</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Tekrar kullanılan ürünleri liste fiyatıyla kaydedin. Tahsilatta seçince bu fiyat gelir; satırda fark yazarak artırıp azaltabilirsiniz.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
          Yeni Ürün
        </Button>
      </Stack>

      <AccountingDataTable<BillingProduct>
        columns={columns}
        rows={rows}
        getRowId={(p) => String(p.id)}
        loading={loading}
        ariaLabel="Tahsilat ürünleri"
        emptyMessage="Henüz ürün eklenmedi."
        defaultRowsPerPage={25}
        onRowClick={(p) => openEdit(p)}
        clickable
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? 'Ürünü düzenle' : 'Yeni ürün'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Liste fiyatı tahsilat kaleminin başlangıç tutarıdır. Örnek: Pavo 4.000 TL. Satışta 6.000
              istiyorsanız kalemde farka 2.000, 3.000 istiyorsanız −1.000 yazılır.
            </Alert>
            <TextField
              label="Ürün adı"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              autoFocus
              placeholder="Örn. Pavo cihazı"
            />
            <TextField
              select
              label="Tür"
              value={form.itemType}
              onChange={(e) => setForm({ ...form, itemType: e.target.value as BillingItemType })}
              fullWidth
            >
              {ITEM_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Liste fiyatı (TRY)"
                value={form.defaultPrice}
                onChange={(e) => setForm({ ...form, defaultPrice: e.target.value })}
                fullWidth
                placeholder="4000"
              />
              <TextField
                label="KDV %"
                value={form.vatRate}
                onChange={(e) => setForm({ ...form, vatRate: e.target.value })}
                fullWidth
              />
            </Stack>
            <TextField
              label="Açıklama (opsiyonel)"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
              }
              label="Aktif"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>İptal</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Ürünü sil</DialogTitle>
        <DialogContent>
          <b>{deleteTarget?.name}</b> silinsin mi? Eski tahsilat kayıtlarındaki kalemler durur.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>İptal</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}>
        <Alert severity="success" onClose={() => setToast(null)}>
          {toast}
        </Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductsTab;
